import { BANNER_SIZES, SDK_ERRORS, SDK_LIMITS, type BannerSize } from '@riz/contracts/sdk';
import { rejectWith, toRizError } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';

export interface BannerRequest {
  /** Element, or an id, whose on-screen rectangle the banner is aligned to. */
  container: string | HTMLElement;
  size?: BannerSize;
}

export interface BannerResult {
  filled: boolean;
  size: { width: number; height: number } | null;
  error?: string;
}

export interface BannerModule {
  request(options: BannerRequest): Promise<BannerResult>;
  requestResponsive(options: { container: string | HTMLElement }): Promise<BannerResult>;
  clear(container: string | HTMLElement): Promise<void>;
  clearAll(): Promise<void>;
}

/**
 * The game nominates a rectangle in its own document; the host renders the
 * banner above it, for the same click-through reason video ads do. Refresh
 * pacing and visibility are enforced server-side, so a game cannot raise its
 * own ceiling by asking more often.
 */
export function createBannerModule(client: SdkClient): BannerModule {
  const lastRequestAt = new Map<string, number>();

  return {
    async request({ container, size = 'medium' }) {
      const capability = client.requireCapability<BannerResult>('ads.banner', 'Banner ads');
      if (capability) return capability;

      if (!BANNER_SIZES[size]) {
        return rejectWith(SDK_ERRORS.bannerInvalidSize, `"${size}" is not a supported banner size.`);
      }
      const key = containerKey(container);
      const cooling = cooldownRemaining(lastRequestAt, key);
      if (cooling > 0) {
        return { filled: false, size: null, error: SDK_ERRORS.bannerCooldown };
      }

      const rect = measure(container);
      if (!rect) return { filled: false, size: null, error: SDK_ERRORS.bannerNotVisible };

      lastRequestAt.set(key, Date.now());
      try {
        const result = await client.call('command', 'banner/request', { container: key, size, rect });
        return { filled: result.filled === true, size: (result.size as BannerResult['size']) ?? BANNER_SIZES[size] };
      } catch (raw) {
        return { filled: false, size: null, error: toRizError(raw).code };
      }
    },

    async requestResponsive({ container }) {
      const capability = client.requireCapability<BannerResult>('ads.banner', 'Banner ads');
      if (capability) return capability;

      const key = containerKey(container);
      if (cooldownRemaining(lastRequestAt, key) > 0) {
        return { filled: false, size: null, error: SDK_ERRORS.bannerCooldown };
      }
      const rect = measure(container);
      if (!rect) return { filled: false, size: null, error: SDK_ERRORS.bannerNotVisible };

      lastRequestAt.set(key, Date.now());
      try {
        const result = await client.call('command', 'banner/request', { container: key, responsive: true, rect });
        return { filled: result.filled === true, size: (result.size as BannerResult['size']) ?? null };
      } catch (raw) {
        return { filled: false, size: null, error: toRizError(raw).code };
      }
    },

    async clear(container) {
      if (!client.capabilities.has('ads.banner')) return;
      const key = containerKey(container);
      lastRequestAt.delete(key);
      await client.call('command', 'banner/clear', { container: key }).catch(() => {});
    },

    async clearAll() {
      if (!client.capabilities.has('ads.banner')) return;
      lastRequestAt.clear();
      await client.call('command', 'banner/clear', { all: true }).catch(() => {});
    },
  };
}

function cooldownRemaining(seen: Map<string, number>, key: string): number {
  const last = seen.get(key);
  if (last === undefined) return 0;
  return Math.max(0, last + SDK_LIMITS.bannerRefreshMs - Date.now());
}

function containerKey(container: string | HTMLElement): string {
  if (typeof container === 'string') return container;
  return container.id || `riz-banner-${Math.abs(hash(container.tagName + (container.className ?? '')))}`;
}

/** Null when the container is missing, detached, or has no visible area. */
function measure(container: string | HTMLElement): { x: number; y: number; width: number; height: number } | null {
  if (typeof document === 'undefined') return { x: 0, y: 0, width: 300, height: 250 };
  const element = typeof container === 'string' ? document.getElementById(container) : container;
  if (!element || !element.isConnected) return null;
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
}

function hash(value: string): number {
  let result = 0;
  for (let index = 0; index < value.length; index += 1) {
    result = (result << 5) - result + value.charCodeAt(index);
    result |= 0;
  }
  return result;
}
